import React, { useEffect, useState } from "react";
import { post, get } from "../../api/api";
import { Card, Divider, Typography } from "@mui/joy";
import { CLayout } from "../../components";
import { getUser } from "../../api/auth";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    BarElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import DashboardOnboarding from "../../components/DashboardOnboarding";
import ScopePremiumContent from "../../components/ScopePremiumContent";
import {AccessTimeFilled} from "@mui/icons-material";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    BarElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

interface FunctionResult {
    name: string;
    parameters: {
        start_date?: string;
        end_date?: string;
        id?: string;
        organizerName?: string;
        organizer_name?: string;
    };
    results: any[];
}

// Composant générique pour afficher un message en cas d'absence de données
const NoDataBlock = ({ message }: { message: string }) => (
    <Card
        variant="outlined"
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 200,
            backgroundColor: "transparent",
            border: "2px dashed rgba(255,255,255,0.5)",
            mt: 2,
            mb: 2,
        }}
    >
        <AccessTimeFilled sx={{ fontSize: 40, color: "#fff" }} />
        <Typography
            level="body-sm"
            sx={{
                color: "#fff",
                textAlign: "center",
                p: 2,
            }}
        >
            {message} <br/>
            Veuillez patienter le temps que les données soient disponibles.
        </Typography>
    </Card>
);

const cardClass = "bg-[#131537] p-4 rounded-lg shadow-lg";

export const VDashboard = () => {
    const user = getUser();

    const [globalAverageInterest, setGlobalAverageInterest] = useState<any>(null);
    const [globalAverageInterestByCategories, setGlobalAverageInterestByCategories] = useState<any>(null);

    // Données brutes renvoyées par getOrganizerInteractionStatsBySubcategoryAndLocation
    const [organizerInteractionStatsRaw, setOrganizerInteractionStatsRaw] = useState<any[]>([]);
    const [organizerInteractionStatsFiltered, setOrganizerInteractionStatsFiltered] = useState<any>(null);

    // Liste des localisations disponibles pour le filtre
    const [locations, setLocations] = useState<string[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<string>("");

    // Données pour les événements personnels (getPersonalOrganizerEvents)
    const [personalOrganizerEventsChart, setPersonalOrganizerEventsChart] = useState<any>(null);

    // Vérification de la présence d'événements
    const [areEvent, setAreEvent] = useState<any>(false);

    const fetchData = async () => {
        try {
            const response = await post({
                url: "functions",
                data: {
                    functions: [
                        {
                            name: "getGlobalAverageInterestPerLocation",
                            parameters: {
                                organizerName: user?.name,
                            },
                        },
                        {
                            name: "getGlobalAverageInterestPerLocationByCategories",
                            parameters: {
                                organizerName: user?.name,
                            },
                        },
                        {
                            name: "getOrganizerInteractionStatsBySubcategoryAndLocation",
                            parameters: {
                                organizerName: user?.name,
                            },
                        },
                        {
                            name: "getPersonalOrganizerEvents",
                            parameters: {
                                organizer_name: user?.name,
                            },
                        },
                    ],
                },
            });
            if (response.status === 201) {
                processFunctionResults(response.data.functions);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const processFunctionResults = (functions: FunctionResult[]) => {
        functions.forEach((func) => {
            if (func.name === "getGlobalAverageInterestPerLocation") {
                setGlobalAverageInterest(func.results);
            } else if (func.name === "getGlobalAverageInterestPerLocationByCategories") {
                const results = func.results;
                if(func.results.length > 0) {
                    const categories = results.map((item: any) => item.category);
                    const likes = results.map((item: any) => item.avgLikes);
                    const views = results.map((item: any) => item.avgViews);
                    const shares = results.map((item: any) => item.avgShares);
                    const interests = results.map((item: any) => item.avgInterests);
                    const chartData = {
                        labels: categories,
                        datasets: [
                            {
                                label: "❤️ Likes",
                                data: likes,
                                backgroundColor: "rgba(255, 99, 132, 0.5)",
                            },
                            {
                                label: "👀 Vues",
                                data: views,
                                backgroundColor: "rgba(75, 192, 192, 0.5)",
                            },
                            {
                                label: "🔁 Partages",
                                data: shares,
                                backgroundColor: "rgba(153, 102, 255, 0.5)",
                            },
                            {
                                label: "🎟 Clics Billetterie",
                                data: interests,
                                backgroundColor: "rgba(255, 206, 86, 0.5)",
                            },
                        ],
                    };
                setGlobalAverageInterestByCategories(chartData);
                }
            } else if (func.name === "getOrganizerInteractionStatsBySubcategoryAndLocation") {
                const results = func.results || [];
                setOrganizerInteractionStatsRaw(results);

                // Récupérer les localisations distinctes pour alimenter la liste de filtrage
                const uniqueLocations = Array.from(new Set(results.map((item: any) => item.location)));
                setLocations(uniqueLocations);

                // Par défaut, sélectionner la première localisation si disponible
                if (uniqueLocations.length > 0) {
                    setSelectedLocation(uniqueLocations[0]);
                }
            } else if (func.name === "getPersonalOrganizerEvents") {
                const personalEvents = func.results || [];

                const labels = personalEvents.map((evt: any) => evt.eventName);
                const totalLikes = personalEvents.map((evt: any) => evt.totalLikes);
                const totalViews = personalEvents.map((evt: any) => evt.totalViews);
                const totalShares = personalEvents.map((evt: any) => evt.totalShares);
                const totalInterests = personalEvents.map((evt: any) => evt.totalInterests);
                const totalInteractions = personalEvents.map((evt: any) => evt.totalInteractions);

                // Vérifier si tous les éléments de chaque dataset sont à 0
                const allZero =
                    totalLikes.every((val: number) => val === 0) &&
                    totalViews.every((val: number) => val === 0) &&
                    totalShares.every((val: number) => val === 0) &&
                    totalInterests.every((val: number) => val === 0) &&
                    totalInteractions.every((val: number) => val === 0);

                // Si toutes les valeurs sont à 0, ne pas setter le chart
                if (allZero) {
                    return;
                }

                const chartData = {
                    labels,
                    datasets: [
                        {
                            label: "❤️ Likes",
                            data: totalLikes,
                            backgroundColor: "rgba(255, 99, 132, 0.5)",
                        },
                        {
                            label: "👀 Vues",
                            data: totalViews,
                            backgroundColor: "rgba(75, 192, 192, 0.5)",
                        },
                        {
                            label: "🔁 Partages",
                            data: totalShares,
                            backgroundColor: "rgba(153, 102, 255, 0.5)",
                        },
                        {
                            label: "🎟 Clics Billetterie",
                            data: totalInterests,
                            backgroundColor: "rgba(255, 206, 86, 0.5)",
                        },
                        {
                            label: "⚡ Total Interactions",
                            data: totalInteractions,
                            backgroundColor: "rgba(54, 162, 235, 0.5)",
                        },
                    ],
                };

                setPersonalOrganizerEventsChart(chartData);
            }
        });
    };

    // Met à jour le graphique en fonction de la localisation sélectionnée
    const updateChartData = (newLocation: string) => {
        if (!organizerInteractionStatsRaw.length) return;

        const filteredData = organizerInteractionStatsRaw.filter(
            (item: any) => item.location === newLocation
        );

        const labels = filteredData.map((item: any) => item.subCategory);
        const avgLikes = filteredData.map((item: any) => item.avgLikes);
        const avgViews = filteredData.map((item: any) => item.avgViews);
        const avgShares = filteredData.map((item: any) => item.avgShares);
        const avgInterests = filteredData.map((item: any) => item.avgInterests);

        const chartData = {
            labels,
            datasets: [
                {
                    label: "❤️ Likes",
                    data: avgLikes,
                    backgroundColor: "rgba(255, 99, 132, 0.5)",
                },
                {
                    label: "👀 Vues",
                    data: avgViews,
                    backgroundColor: "rgba(75, 192, 192, 0.5)",
                },
                {
                    label: "🔁 Partages",
                    data: avgShares,
                    backgroundColor: "rgba(153, 102, 255, 0.5)",
                },
                {
                    label: "🎟 Clics Billetterie",
                    data: avgInterests,
                    backgroundColor: "rgba(255, 206, 86, 0.5)",
                },
            ],
        };
        setOrganizerInteractionStatsFiltered(chartData);
    };

    const fetchEvent = () => {
        const urls = [`events?_end=9&_start=1&date_filter=PAST`,`events?_end=9&_start=1&date_filter=FUTURE`];
        urls.forEach((url) => {
            void get({ url })
                .then((res) => {
                    if (res.data.length > 0) {
                        setAreEvent(true);
                    }
                })
                .catch((err) => console.log(err));
        })
    };

    useEffect(() => {
        fetchEvent();
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedLocation) {
            updateChartData(selectedLocation);
        }
    }, [selectedLocation, organizerInteractionStatsRaw]);

    useEffect(() => {
        console.log(globalAverageInterestByCategories);
    }, [globalAverageInterestByCategories]);

    return (
        <CLayout>
            <section id="VDashboard">
                <div
                    className="bg-[#0E102B] text-white min-h-screen p-6 mt-3"
                    style={{ borderRadius: "10px" }}
                >
                    {!areEvent ? (
                        <DashboardOnboarding />
                    ) : (
                        <>
                            {/* Statistiques Globales par Localisation */}
                            {globalAverageInterest && globalAverageInterest.length > 0 ? (
                                <>
                                    <Typography
                                        level="h3"
                                        className="!font-bold"
                                        style={{ color: "white" }}
                                    >
                                        🌍 Performances par zone
                                    </Typography>
                                    <Divider
                                        orientation="horizontal"
                                        className="!mb-5 !mt-1"
                                        style={{ backgroundColor: "white", height: "1px" }}
                                    />
                                    <ScopePremiumContent scope="dashboard_freemium_statistics">
                                        {globalAverageInterest.map((item: any, index: number) => (
                                            <div key={index}>
                                                <Typography className="!mb-2" style={{ color: "white" }}>
                                                    📈 Statistique moyenne sur{" "}
                                                    <span className="!font-bold">{item.localisation}</span>
                                                </Typography>
                                                <div className="grid grid-cols-4 gap-4 mb-6">
                                                    <Card className={cardClass}>
                                                        <Typography level="h3" className="!text-3xl font-bold text-center">
                                                            {item.avgViews ? item.avgViews.toFixed(2) : "0"}
                                                        </Typography>
                                                        <Typography level="body-sm" className="!text-xs font-semibold text-center">
                                                            👀 Vues
                                                        </Typography>
                                                    </Card>
                                                    <Card className={cardClass}>
                                                        <Typography level="h3" className="!text-3xl font-bold text-center">
                                                            {item.avgLikes ? item.avgLikes.toFixed(2) : "0"}
                                                        </Typography>
                                                        <Typography level="body-sm" className="!text-xs font-semibold text-center">
                                                            ❤️ Likes
                                                        </Typography>
                                                    </Card>
                                                    <Card className={cardClass}>
                                                        <Typography level="h3" className="!text-3xl font-bold text-center">
                                                            {item.avgShares ? item.avgShares.toFixed(2) : "0"}
                                                        </Typography>
                                                        <Typography level="body-sm" className="!text-xs font-semibold text-center">
                                                            🔁 Partages
                                                        </Typography>
                                                    </Card>
                                                    <Card className={cardClass}>
                                                        <Typography level="h3" className="!text-3xl font-bold text-center">
                                                            {item.avgInterests ? item.avgInterests.toFixed(2) : "0"}
                                                        </Typography>
                                                        <Typography level="body-sm" className="!text-xs font-semibold text-center">
                                                            🎟 Clics Billetterie
                                                        </Typography>
                                                    </Card>
                                                </div>
                                            </div>
                                        ))}
                                    </ScopePremiumContent>
                                </>
                            ) : (
                                <NoDataBlock message="Aucune statistique de zones pour le moment." />
                            )}

                            {/* Statistiques Globales par Catégorie */}
                            <Typography
                                level="h3"
                                className="!font-bold"
                                style={{ color: "white" }}
                            >
                                📊 Performances par catégorie
                            </Typography>
                            <Divider
                                orientation="horizontal"
                                className="!mb-5 !mt-1"
                                style={{ backgroundColor: "white", height: "1px" }}
                            />
                            <ScopePremiumContent scope="dashboard_freemium_statistics">
                                {globalAverageInterestByCategories &&
                                globalAverageInterestByCategories.datasets.length > 0 ? (
                                    <Card className={cardClass}>
                                        <Bar data={globalAverageInterestByCategories} />
                                    </Card>
                                ) : (
                                    <NoDataBlock message="Aucune statistique par catégorie pour le moment." />
                                )}
                            </ScopePremiumContent>

                            {/* Statistiques d'interactions par Sous-catégorie & Localisation (filtrage) */}
                            {organizerInteractionStatsFiltered &&
                            organizerInteractionStatsFiltered.datasets.length > 0 ? (
                                <>
                                    <Typography
                                        level="h3"
                                        className="!font-bold !mt-4"
                                        style={{ color: "white" }}
                                    >
                                        📌 Performances zones & catégories
                                    </Typography>
                                    <Divider
                                        orientation="horizontal"
                                        className="!mb-5 !mt-1"
                                        style={{ backgroundColor: "white", height: "1px" }}
                                    />
                                    <ScopePremiumContent scope="dashboard_premium_statistics">
                                        <div className="mb-4">
                                            <label htmlFor="locationSelect" className="mr-2">
                                                Filtrer par localisation :
                                            </label>
                                            <select
                                                id="locationSelect"
                                                className="text-black p-1 rounded"
                                                value={selectedLocation}
                                                onChange={(e) => setSelectedLocation(e.target.value)}
                                            >
                                                {locations.map((loc) => (
                                                    <option key={loc} value={loc}>
                                                        {loc}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <Card className={cardClass}>
                                            <Bar data={organizerInteractionStatsFiltered} />
                                        </Card>
                                    </ScopePremiumContent>
                                </>
                            ) : (
                                <NoDataBlock message="Aucune statistique d'interaction pour cette localisation." />
                            )}

                            {/* Statistiques des Événements Personnels */}
                            <Typography
                                level="h3"
                                className="!font-bold !mt-4"
                                style={{ color: "white" }}
                            >
                                ⚡ Performance sur vos Events
                            </Typography>
                            <Divider
                                orientation="horizontal"
                                className="!mb-5 !mt-1"
                                style={{ backgroundColor: "white", height: "1px" }}
                            />
                            <ScopePremiumContent scope="dashboard_premium_statistics">
                                {personalOrganizerEventsChart &&
                                personalOrganizerEventsChart.datasets.length > 0 ? (
                                    <Card className={cardClass}>
                                        <Bar data={personalOrganizerEventsChart} />
                                    </Card>
                                ) : (
                                    <NoDataBlock message="Aucune donnée d'événements personnels pour le moment." />
                                )}
                            </ScopePremiumContent>
                        </>
                    )}
                </div>
            </section>
        </CLayout>
    );
};

export default VDashboard;